// PartA2.js - Part A2 Self-Evaluation & Goals component with pagination

import React, { useState, useEffect } from 'react';
import { Button, message, Pagination } from 'antd';
import { SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import { getAccessToken } from '../../authClient';
import PartA2SelfEvaluation from './components/PartA2SelfEvaluation';
import PartA2Goals from './components/PartA2Goals';
import { API_CONFIG } from '../../config/api';

const API_BASE_URL = API_CONFIG.BASE_URL;

const PartA2 = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);

    // Form data state matching the images exactly
    const [formData, setFormData] = useState({
        // Self-Evaluation Performance Sliders (1-10 scale)
        last_year_performance: 5,
        teacher_thought_efforts: 5,

        // New Things and Improvements
        try_new_things: '',
        work_harder_on: '',

        // Career Aspirations (4 items each)
        career_aspirations: ['', '', '', ''],
        to_fulfill_aspirations: ['', '', '', ''],

        // School Goals - Past
        school_goal_last_year: '',
        school_goal_importance: '',
        school_goal_status: '', // 'accomplished' or 'still_working'

        // School Goals - New
        school_goal_this_year: '',
        school_goal_achievement_indicator: '',

        // Action Steps for School Goal (timeline)
        school_goal_week_action: '',
        school_goal_six_weeks_action: '',
        school_goal_six_months_action: '',

        // Support System for School Goal (3 items each)
        school_goal_strengths: ['', '', ''],
        school_goal_home_support: ['', '', ''],
        school_goal_school_support: ['', '', ''],

        // Outside School Goals - Past
        outside_goal_last_year: '',
        outside_goal_status: '', // 'accomplished' or 'still_working'

        // Outside School Goals - New
        outside_goal_this_year: '',
        outside_goal_achievement_indicator: '',

        // Action Steps for Outside Goal (timeline)
        outside_goal_week_action: '',
        outside_goal_six_weeks_action: '',
        outside_goal_six_months_action: '',

        // Support System for Outside Goal (3 items each)
        outside_goal_strengths: ['', '', ''],
        outside_goal_home_support: ['', '', ''],
        outside_goal_school_support: ['', '', ''],

        // Goal Importance and Reflection
        outside_goal_importance: '',
        achieved_goals_proud_of: ''
    });

    useEffect(() => {
        loadExistingData();
    }, []);

    const loadExistingData = async () => {
        try {
            const token = getAccessToken();
            const response = await fetch(`${API_BASE_URL}/secondary/user/hpc-profile/part-a2`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.exists && data.part_a_2) {
                    setFormData(prevData => ({
                        ...prevData,
                        ...data.part_a_2
                    }));
                }
            }
        } catch (error) {
            console.error('Error loading existing data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleArrayInputChange = (field, index, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: prev[field].map((item, i) => i === index ? value : item)
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const token = getAccessToken();
            const response = await fetch(`${API_BASE_URL}/secondary/user/hpc-profile/part-a2`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });

            if (response.ok) {
                message.success('Part A2 saved successfully!');
            } else {
                throw new Error('Failed to save Part A2');
            }
        } catch (error) {
            console.error('Error saving Part A2:', error);
            message.error('Failed to save Part A2');
        } finally {
            setSaving(false);
        }
    };

    const renderCurrentPage = () => {
        switch (currentPage) {
            case 1:
                return (
                    <PartA2SelfEvaluation
                        formData={formData}
                        handleInputChange={handleInputChange}
                        handleArrayInputChange={handleArrayInputChange}
                    />
                );
            case 2:
                return (
                    <PartA2Goals
                        formData={formData}
                        handleInputChange={handleInputChange}
                        handleArrayInputChange={handleArrayInputChange}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div className="part-a2-container">
            {/* Page Content */}
            {renderCurrentPage()}

            {/* Navigation and Save Controls */}
            <div className="page-controls">
                <div className="pagination-section">
                    <Pagination
                        current={currentPage}
                        total={2}
                        pageSize={1}
                        onChange={setCurrentPage}
                        showSizeChanger={false}
                        showQuickJumper={false}
                        showTotal={(total, range) => `Page ${range[0]} of ${total}`}
                    />
                </div>

                <div className="save-section">
                    <Button
                        type="primary"
                        size="large"
                        icon={<SaveOutlined />}
                        onClick={handleSave}
                        loading={saving}
                        className="save-button"
                    >
                        {saving ? 'Saving...' : 'Save Part A2'}
                    </Button>

                    <Button
                        size="large"
                        icon={<ReloadOutlined />}
                        onClick={loadExistingData}
                        className="reload-button"
                        style={{ marginLeft: '10px' }}
                    >
                        Reload Data
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default PartA2;